import React, { useState, useEffect, useCallback } from 'react';
import {
  Form, Input, InputNumber, Button, Card, Row, Col, message, Space, Typography,
  Checkbox, Divider, Descriptions, Tag, Spin, Modal,
} from 'antd';
import {
  SaveOutlined, ArrowLeftOutlined, FileDoneOutlined, DownloadOutlined,
  EyeOutlined, CaretRightOutlined, CaretDownOutlined,
} from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import api from '../../api/axios';

const { Title, Text } = Typography;
const { TextArea } = Input;

const SatisSozlesmeForm = () => {
  const [form] = Form.useForm();
  const [satilikData, setSatilikData] = useState(null);
  const [maddelerState, setMaddelerState] = useState([]); // { id, maddeBaslik, maddeIcerik, siraNo, selected, customIcerik, expanded }
  const [existingSozlesme, setExistingSozlesme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [wordLoading, setWordLoading] = useState(false);
  const [acLoading, setAcLoading] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const { satilikId } = useParams();
  const navigate = useNavigate();

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [satilikRes, maddelerRes, sozlesmeRes] = await Promise.allSettled([
        api.get(`/satilik/${satilikId}`),
        api.get('/satissozlesme/maddeler'),
        api.get(`/satissozlesme/satilik/${satilikId}`),
      ]);

      let standartMaddeler = [];
      if (maddelerRes.status === 'fulfilled') {
        const arr = Array.isArray(maddelerRes.value.data) ? maddelerRes.value.data : maddelerRes.value.data.data || [];
        standartMaddeler = arr;
      }

      if (satilikRes.status === 'fulfilled') {
        setSatilikData(satilikRes.value.data.data || satilikRes.value.data);
      }

      // Mevcut sözleşme varsa detay tablosundan yükle (C# mantığı)
      if (sozlesmeRes.status === 'fulfilled' && sozlesmeRes.value.data) {
        const raw = sozlesmeRes.value.data;
        const sozlesme = Array.isArray(raw) ? raw[0] : (raw.data || raw);

        if (sozlesme && sozlesme.id) {
          setExistingSozlesme(sozlesme);
          // Kapora doluysa ve bağlanma parası 0/boşsa → kapora'yı kullan
          // Bağlanma parası zaten girilmişse → dokunma
          const satilikItem = satilikRes.status === 'fulfilled'
            ? (satilikRes.value.data.data || satilikRes.value.data)
            : null;
          const kapora = satilikItem?.kapora || 0;
          const mevcutBaglanma = sozlesme.baglanmaParasi || 0;
          const baglanmaParasiValue = (mevcutBaglanma > 0) ? mevcutBaglanma : (kapora > 0 ? kapora : null);
          form.setFieldsValue({
            sozlesmeNo: sozlesme.sozlesmeNo,
            ekMaddeler: sozlesme.ekMaddeler,
            satisTutari: sozlesme.satisTutari,
            baglanmaParasi: baglanmaParasiValue,
            saticiKomisyonOrani: sozlesme.saticiKomisyonOrani,
            aliciKomisyonOrani: sozlesme.aliciKomisyonOrani,
          });

          // Eski sözleşme: TÜM standart maddeler gösterilir,
          // detay tablosunda olanlar seçili+özel içerikli, olmayanlar seçisiz
          try {
            const detayRes = await api.get(`/satissozlesme/${sozlesme.id}/maddeler`);
            const detayArr = Array.isArray(detayRes.data) ? detayRes.data : detayRes.data.data || [];

            const savedMap = {};
            detayArr.forEach((d) => {
              const key = d.maddeId || d.id;
              if (key) savedMap[key] = d;
            });

            const stateArr = standartMaddeler.map((m) => {
              const saved = savedMap[m.id];
              return {
                id: m.id,
                maddeBaslik: m.maddeBaslik,
                maddeIcerik: m.maddeIcerik,
                siraNo: m.siraNo,
                selected: !!saved,
                customIcerik: saved ? saved.maddeIcerik : m.maddeIcerik,
                expanded: false,
              };
            });
            setMaddelerState(stateArr);
          } catch {
            setMaddelerState(standartMaddeler.map((m) => ({
              id: m.id,
              maddeBaslik: m.maddeBaslik,
              maddeIcerik: m.maddeIcerik,
              siraNo: m.siraNo,
              selected: true,
              customIcerik: m.maddeIcerik,
              expanded: false,
            })));
          }
          setLoading(false);
          if (!form.getFieldValue('sozlesmeNo')) {
            const year = dayjs().year();
            form.setFieldValue('sozlesmeNo', `SS-${year}-${String(satilikId).padStart(3, '0')}`);
          }
          return;
        }
      }

      // Yeni sözleşme: standart maddelerden yükle, hepsi seçili
      const stateArr = standartMaddeler.map((m) => ({
        id: m.id,
        maddeBaslik: m.maddeBaslik,
        maddeIcerik: m.maddeIcerik,
        siraNo: m.siraNo,
        selected: true,
        customIcerik: m.maddeIcerik,
        expanded: false,
      }));
      setMaddelerState(stateArr);

      // Yeni sözleşmede bağlanma parasını satilik kapora'sından otomatik doldur
      if (satilikRes.status === 'fulfilled') {
        const satilikItem = satilikRes.value.data.data || satilikRes.value.data;
        if (satilikItem?.kapora) {
          form.setFieldValue('baglanmaParasi', satilikItem.kapora);
        }
      }

    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }

    if (!form.getFieldValue('sozlesmeNo')) {
      const year = dayjs().year();
      form.setFieldValue('sozlesmeNo', `SS-${year}-${String(satilikId).padStart(3, '0')}`);
    }
  }, [satilikId, form]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const toggleSelect = (id) => {
    setMaddelerState((prev) =>
      prev.map((m) => m.id === id ? { ...m, selected: !m.selected } : m)
    );
  };

  const toggleExpand = (id) => {
    setMaddelerState((prev) =>
      prev.map((m) => m.id === id ? { ...m, expanded: !m.expanded } : m)
    );
  };

  const updateCustomIcerik = (id, value) => {
    setMaddelerState((prev) =>
      prev.map((m) => m.id === id ? { ...m, customIcerik: value } : m)
    );
  };

  const onFinish = async (values) => {
    setSaving(true);
    try {
      const selectedMaddeler = maddelerState.filter((m) => m.selected);
      const maddelerIcerik = selectedMaddeler.map((m) => ({
        maddeId: m.id,
        maddeBaslik: m.maddeBaslik,
        maddeIcerik: m.customIcerik || m.maddeIcerik,
        siraNo: m.siraNo,
      }));

      const payload = {
        satilikId: parseInt(satilikId),
        sozlesmeNo: values.sozlesmeNo,
        ekMaddeler: values.ekMaddeler,
        satisTutari: values.satisTutari,
        baglanmaParasi: values.baglanmaParasi,
        saticiKomisyonOrani: values.saticiKomisyonOrani,
        aliciKomisyonOrani: values.aliciKomisyonOrani,
        seciliMaddeler: selectedMaddeler.map((m) => m.id).join(','),
        maddelerIcerik,
      };

      if (existingSozlesme) {
        await api.put(`/satissozlesme/${existingSozlesme.id}`, payload);
        message.success('Sözleşme güncellendi');
      } else {
        await api.post('/satissozlesme', payload);
        message.success('Sözleşme oluşturuldu');
      }
      navigate('/satis-sozlesmesi');
    } catch (err) {
      message.error(err.response?.data?.message || 'Kayıt başarısız');
    } finally {
      setSaving(false);
    }
  };

  const downloadBlob = (blobData, headers) => {
    const cd = headers['content-disposition'] || '';
    const match = cd.match(/filename\*?=(?:UTF-8'')?([^;\r\n]+)/i);
    const filename = match ? decodeURIComponent(match[1].replace(/"/g, '')) : `SatisSozlesmesi_${satilikId}.docx`;
    const url = window.URL.createObjectURL(new Blob([blobData]));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleBlobError = async (err, fallback) => {
    let errMsg = fallback;
    try {
      if (err.response?.data instanceof Blob) {
        const text = await err.response.data.text();
        const parsed = JSON.parse(text);
        errMsg = parsed.error || fallback;
      } else if (err.response?.data?.error) {
        errMsg = err.response.data.error;
      }
    } catch {}
    message.error(errMsg);
  };

  const savePayload = () => {
    const values = form.getFieldsValue();
    const selectedMaddeler = maddelerState.filter((m) => m.selected);
    const maddelerIcerik = selectedMaddeler.map((m) => ({
      maddeId: m.id,
      maddeBaslik: m.maddeBaslik,
      maddeIcerik: m.customIcerik || m.maddeIcerik,
      siraNo: m.siraNo,
    }));
    return {
      satilikId: parseInt(satilikId),
      sozlesmeNo: values.sozlesmeNo,
      ekMaddeler: values.ekMaddeler,
      satisTutari: values.satisTutari,
      baglanmaParasi: values.baglanmaParasi,
      saticiKomisyonOrani: values.saticiKomisyonOrani,
      aliciKomisyonOrani: values.aliciKomisyonOrani,
      seciliMaddeler: selectedMaddeler.map((m) => m.id).join(','),
      maddelerIcerik,
    };
  };

  const handleWordIndir = async () => {
    setWordLoading(true);
    try {
      // Önce kaydet / güncelle
      const payload = savePayload();
      if (existingSozlesme) {
        await api.put(`/satissozlesme/${existingSozlesme.id}`, payload);
      } else {
        const res = await api.post('/satissozlesme', payload);
        const created = res.data?.data || res.data;
        if (created?.id) setExistingSozlesme(created);
      }
      // Sonra Word üret
      const response = await api.get(`/satissozlesme/generate-word/${satilikId}`, { responseType: 'blob' });
      downloadBlob(response.data, response.headers);
    } catch (err) {
      await handleBlobError(err, 'Word belgesi oluşturulamadı');
    } finally {
      setWordLoading(false);
    }
  };

  const handleAc = async () => {
    setAcLoading(true);
    try {
      const response = await api.get(`/satissozlesme/saved-word/${satilikId}`, { responseType: 'blob' });
      downloadBlob(response.data, response.headers);
    } catch (err) {
      await handleBlobError(err, 'Kaydedilmiş dosya bulunamadı. Önce "Kaydet ve İndir" yapınız.');
    } finally {
      setAcLoading(false);
    }
  };

  const formatMoney = (val) => {
    if (!val && val !== 0) return '-';
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(val);
  };

  const turkceYaz = (sayi) => {
    if (!sayi && sayi !== 0) return '';
    sayi = Math.floor(Number(sayi));
    if (isNaN(sayi) || sayi < 0) return '';
    if (sayi === 0) return 'SIFIR';
    const birler = ['', 'BİR', 'İKİ', 'ÜÇ', 'DÖRT', 'BEŞ', 'ALTI', 'YEDİ', 'SEKİZ', 'DOKUZ'];
    const onlar = ['', 'ON', 'YİRMİ', 'OTUZ', 'KIRK', 'ELLİ', 'ALTMIŞ', 'YETMİŞ', 'SEKSEN', 'DOKSAN'];
    const ucHane = (n) => {
      const p = [];
      const y = Math.floor(n / 100), o = Math.floor((n % 100) / 10), b = n % 10;
      if (y === 1) p.push('YÜZ'); else if (y > 1) p.push(birler[y] + 'YÜZ');
      if (o > 0) p.push(onlar[o]);
      if (b > 0) p.push(birler[b]);
      return p.join('');
    };
    const parts = [];
    const milyar = Math.floor(sayi / 1000000000);
    const milyon = Math.floor((sayi % 1000000000) / 1000000);
    const bin = Math.floor((sayi % 1000000) / 1000);
    const kalan = sayi % 1000;
    if (milyar > 0) parts.push(ucHane(milyar) + 'MİLYAR');
    if (milyon > 0) parts.push(ucHane(milyon) + 'MİLYON');
    if (bin > 0) parts.push(bin === 1 ? 'BİN' : ucHane(bin) + 'BİN');
    if (kalan > 0) parts.push(ucHane(kalan));
    return parts.join('');
  };

  const selectedMaddeler = maddelerState.filter((m) => m.selected);

  const buildSatisValueMap = (data) => {
    if (!data) return {};
    const fmt = (v) => v ? Number(v).toLocaleString('tr-TR', { minimumFractionDigits: 2 }) : '0,00';
    const fmtDate = (v) => v ? dayjs(v).format('DD.MM.YYYY') : '';
    const satisTutari = form.getFieldValue('satisTutari') || data.anapara || 0;
    const baglanmaParasi = form.getFieldValue('baglanmaParasi') || 0;
    const saticiKomisyon = form.getFieldValue('saticiKomisyonOrani') || 0;
    const aliciKomisyon = form.getFieldValue('aliciKomisyonOrani') || 0;
    return {
      IL: data.ilAdi || '',
      ILCE: data.ilceAdi || '',
      ADRES: data.adres || '',
      SATICI: data.saticiAdi || '',
      SATICI_AD: data.saticiAdi || '',
      SATICI_TC: data.saticiTc || '',
      ALICI: data.aliciAdi || '',
      ALICI_AD: data.aliciAdi || '',
      ALICI_TC: data.aliciTc || '',
      KOMISYONCU: data.komisyoncuAdi || '',
      MEVKI: data.mevki || '',
      MAHALLE: data.mevki || '',
      ADA: data.adaNo || '',
      PARSEL: data.parselNo || '',
      ADA_NO: data.adaNo || '',
      PARSEL_NO: data.parselNo || '',
      DAIRE_NO: data.daireNo || '',
      NEVI: data.nevi || '',
      ANAPARA: fmt(data.anapara),
      PESINAT: fmt(data.pesinat),
      KALAN: fmt(data.kalan),
      DB_KREDI: fmt(data.dbkredi),
      KAPORA: fmt(data.kapora),
      TAKSIT_SAYISI: data.taksitSayisi ? String(data.taksitSayisi) : '',
      TAKSIT_TUTARI: fmt(data.taksitTutari),
      TAKSIT_BASLANGIC: fmtDate(data.taksitBaslangicTarihi),
      EMLAKTIPI: data.emlakTipiAdi || '',
      SATIS_TUTARI: fmt(satisTutari),
      SATIS_TUTARI_YAZI: turkceYaz(satisTutari),
      BAGLANMA_PARASI: fmt(baglanmaParasi),
      BAGLANMA_PARASI_YAZI: turkceYaz(baglanmaParasi),
      KOMISYON_SATICI: fmt(satisTutari * saticiKomisyon / 100),
      KOMISYON_ALICI: fmt(satisTutari * aliciKomisyon / 100),
      SATICI_KOMISYON_ORANI: saticiKomisyon ? String(saticiKomisyon) : '',
      ALICI_KOMISYON_ORANI: aliciKomisyon ? String(aliciKomisyon) : '',
      TARIH: dayjs().format('DD.MM.YYYY'),
    };
  };

  const resolvePlaceholders = (text, valueMap) => {
    if (!text) return '';
    return text.replace(/\{([A-Z0-9_]+)\}/g, (match, key) =>
      key in valueMap ? valueMap[key] : match
    );
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 100 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Space>
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/satis-sozlesmesi')}>Geri</Button>
            <Title level={4} style={{ margin: 0 }}>
              <FileDoneOutlined /> Satış Sözleşmesi
              {existingSozlesme && <Tag color="green" style={{ marginLeft: 8 }}>Mevcut Sözleşme</Tag>}
            </Title>
          </Space>
        </Col>
      </Row>

      {satilikData && (
        <Card title="Satılık Kaydı Bilgileri" style={{ marginBottom: 16, borderRadius: 8 }}>
          <Descriptions bordered size="small" column={{ xs: 1, sm: 2, md: 3 }}>
            <Descriptions.Item label="Satıcı">{satilikData.saticiAdi || '-'}</Descriptions.Item>
            <Descriptions.Item label="Alıcı">{satilikData.aliciAdi || '-'}</Descriptions.Item>
            <Descriptions.Item label="Emlak Tipi">{satilikData.emlakTipiAdi || '-'}</Descriptions.Item>
            <Descriptions.Item label="İl">{satilikData.ilAdi || '-'}</Descriptions.Item>
            <Descriptions.Item label="İlçe">{satilikData.ilceAdi || '-'}</Descriptions.Item>
            <Descriptions.Item label="Ana Para">{formatMoney(satilikData.anapara)}</Descriptions.Item>
            <Descriptions.Item label="Peşinat">{formatMoney(satilikData.pesinat)}</Descriptions.Item>
            <Descriptions.Item label="Kalan">{formatMoney(satilikData.kalan)}</Descriptions.Item>
          </Descriptions>
        </Card>
      )}

      <Form form={form} layout="vertical" onFinish={onFinish}>
        <Card title="Sözleşme Detayları" style={{ marginBottom: 16, borderRadius: 8 }}>
          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item name="sozlesmeNo" label="Sözleşme No" rules={[{ required: true, message: 'Zorunlu' }]}>
                <Input placeholder="SS-2024-001" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="satisTutari" label="Satış Tutarı (₺)">
                <InputNumber
                  min={0} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="baglanmaParasi" label="Bağlanma Parası (₺)">
                <InputNumber
                  min={0} style={{ width: '100%' }}
                  formatter={(v) => `${v}`.replace(/\B(?=(\d{3})+(?!\d))/g, '.')}
                  parser={(v) => v.replace(/\./g, '')}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Form.Item name="saticiKomisyonOrani" label="Satıcı Komisyon (%)">
                <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
            <Col xs={12} md={6}>
              <Form.Item name="aliciKomisyonOrani" label="Alıcı Komisyon (%)">
                <InputNumber min={0} max={100} step={0.1} style={{ width: '100%' }} placeholder="0.00" />
              </Form.Item>
            </Col>
          </Row>

          <Divider orientation="left">
            Standart Maddeler
            <Text type="secondary" style={{ fontSize: 12, marginLeft: 8 }}>
              ({selectedMaddeler.length} seçili)
            </Text>
          </Divider>

          {maddelerState.length === 0 && (
            <Text type="secondary">Standart madde bulunamadı</Text>
          )}

          {maddelerState.map((madde) => (
            <Card
              key={madde.id}
              size="small"
              style={{
                marginBottom: 8,
                borderRadius: 6,
                borderColor: madde.selected ? '#1677ff' : '#d9d9d9',
                background: madde.selected ? '#f0f7ff' : '#fff',
              }}
              bodyStyle={{ padding: '8px 12px' }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Checkbox
                  checked={madde.selected}
                  onChange={() => toggleSelect(madde.id)}
                />
                <Text
                  strong
                  style={{ flex: 1, cursor: 'pointer' }}
                  onClick={() => toggleExpand(madde.id)}
                >
                  {madde.maddeBaslik}
                </Text>
                <Button
                  type="text"
                  size="small"
                  icon={madde.expanded ? <CaretDownOutlined /> : <CaretRightOutlined />}
                  onClick={() => toggleExpand(madde.id)}
                />
              </div>
              {madde.expanded && (
                <div style={{ marginTop: 8 }}>
                  <TextArea
                    rows={4}
                    value={madde.customIcerik}
                    onChange={(e) => updateCustomIcerik(madde.id, e.target.value)}
                    placeholder="Madde içeriği..."
                    style={{ fontSize: 13 }}
                  />
                  {satilikData && (
                    <div style={{ marginTop: 6, padding: '6px 10px', background: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4 }}>
                      <Text type="secondary" style={{ fontSize: 11, display: 'block', marginBottom: 3 }}>Çözümlenmiş Görünüm:</Text>
                      <Text style={{ fontSize: 12, whiteSpace: 'pre-wrap', color: '#333' }}>
                        {resolvePlaceholders(madde.customIcerik, buildSatisValueMap(satilikData))}
                      </Text>
                    </div>
                  )}
                </div>
              )}
            </Card>
          ))}

          <Divider orientation="left">Ek Maddeler</Divider>
          <Form.Item name="ekMaddeler" label="Ek Maddeler">
            <TextArea rows={6} placeholder="Ek sözleşme maddelerini buraya girin..." />
          </Form.Item>
        </Card>

        <Row justify="end">
          <Space>
            <Button onClick={() => navigate('/satis-sozlesmesi')}>İptal</Button>
            <Button
              icon={<EyeOutlined />}
              onClick={handleAc}
              loading={acLoading}
            >
              Aç
            </Button>
            <Button
              icon={<DownloadOutlined />}
              onClick={handleWordIndir}
              loading={wordLoading}
            >
              {existingSozlesme ? 'Güncelle ve İndir' : 'Oluştur ve İndir'}
            </Button>
            <Button type="primary" htmlType="submit" loading={saving} icon={<SaveOutlined />}>
              {existingSozlesme ? 'Güncelle' : 'Sözleşme Oluştur'}
            </Button>
          </Space>
        </Row>
      </Form>

      <Modal
        title="Sözleşme Önizleme"
        open={previewOpen}
        onCancel={() => setPreviewOpen(false)}
        footer={[
          <Button key="close" onClick={() => setPreviewOpen(false)}>Kapat</Button>,
        ]}
        width={700}
      >
        <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
          {selectedMaddeler.map((m, i) => (
            <div key={m.id} style={{ marginBottom: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {i + 1}. {m.maddeBaslik}
              </Text>
              <Text style={{ whiteSpace: 'pre-wrap', color: '#555' }}>
                {resolvePlaceholders(m.customIcerik || m.maddeIcerik, buildSatisValueMap(satilikData))}
              </Text>
            </div>
          ))}
          {form.getFieldValue('ekMaddeler') && (
            <div style={{ marginTop: 16, borderTop: '1px solid #eee', paddingTop: 16 }}>
              <Text strong style={{ display: 'block', marginBottom: 8 }}>Ek Maddeler:</Text>
              <Text style={{ whiteSpace: 'pre-wrap', color: '#555' }}>
                {form.getFieldValue('ekMaddeler')}
              </Text>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default SatisSozlesmeForm;